import {Trans} from '@lingui/macro';
import {isString} from 'lodash';
import React, {FC, Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {ellipsis, flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {
  isRegexPart,
  RegexPart,
  StringOrTemplate,
} from '../../../../settings/editors/commons/stringTemplateModel';
import {getLabelForRegex, getPropertyLabel} from '../../../../settings/editors/expressions/expressionLabels';
import {PreExpression} from '../../../../settings/editors/expressions/expressionModel';
import {ExpressionRef} from '../../../../settings/editors/expressions/expressionRefModel';
import {StringResult} from '../../../../settings/editors/expressions/infer/expressionResultModel';
import {ExpressionPill} from '../../../../settings/rules/ruleEditor/inputs/expressionPill';
import {ChildrenCell} from './childrenCell';
import {TableCellProps} from './tableCell';
import {StyledTextCellValueDiv} from './textCell';

/*
 * Props.
 */

interface StringTemplateCellProps extends TableCellProps<StringOrTemplate> {
  expressions: ReadonlyArray<PreExpression>;
}

/*
 * Styles.
 */

const StringTemplateCellValueDiv = styled(StyledTextCellValueDiv)`
  ${flexCenter};
  padding: 0 12px;
`;

const StyledRegexSpan = styled.span`
  ${flexCenter};
  width: min-content;
  max-width: 100%;
  height: 22px;
  line-height: 16px;
  background-color: ${(p) => p.theme.palette.blue.shade20};
  color: ${(p) => p.theme.palette.blue.shade40};
  border-radius: 4px;
`;

const StyledRegexText = styled.div`
  padding: 3px 6px;
  ${ellipsis()};
`;

/*
 * Component.
 */

export const StringTemplateCell: FC<StringTemplateCellProps> = (props) => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    expressions,
    settings,
    index,
    value,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;

  return (
    <ChildrenCell
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      hasPadding
    >
      <StringTemplateCellValueDiv $isSelected={Boolean(isSelected)}>
        {value && renderTemplatePreview(value, expressions)}
      </StringTemplateCellValueDiv>
    </ChildrenCell>
  );
};

/*
 * Helpers.
 */

export function renderTemplatePreview(
  template: StringOrTemplate,
  expressions: ReadonlyArray<PreExpression>,
): ReactNode {
  if (isString(template)) {
    return [template];
  }

  return template.template.map((part) => {
    if (isString(part)) {
      return <Fragment key={part}>{part}</Fragment>;
    }

    if (isRegexPart(part)) {
      return renderRegexPill(part);
    }

    const label = renderExpressionName(part, expressions);
    return <ExpressionPill key={String(label)} label={label} type={StringResult} disableHover />;
  });
}

function renderRegexPill(regexPart: RegexPart): ReactNode {
  const label = getLabelForRegex(regexPart);

  return (
    <StyledRegexSpan key={label}>
      <StyledRegexText>{label}</StyledRegexText>
    </StyledRegexSpan>
  );
}

function renderExpressionName(ref: ExpressionRef, expressions: ReadonlyArray<PreExpression>) {
  const staticLabel = getPropertyLabel(ref.expression);
  if (staticLabel) {
    return staticLabel;
  }

  const expression = expressions.find((e) => e.id === ref.expression);
  if (expression) {
    return expression.name;
  }

  return <Trans id="string-template-cell-missing-variable">Missing variable</Trans>;
}
